'use client';
import { useState, useEffect } from 'react';
 import Icon from'@/components/ui/AppIcon';

interface PaymentMethod {
  id: string
  name: string
  icon: 'CreditCardIcon' | 'WalletIcon' | 'BanknotesIcon'
}

const paymentMethods: PaymentMethod[] = [
  { id: 'payment_card', name: 'Credit/Debit Card', icon: 'CreditCardIcon' },
  { id: 'payment_wallet', name: 'Digital Wallet', icon: 'WalletIcon' },
  { id: 'payment_cash', name: 'Cash', icon: 'BanknotesIcon' },
]

export default function PaymentSection() {
  const [mounted, setMounted] = useState(false)
  const [selectedPayment, setSelectedPayment] = useState<string>('payment_card')
  const [promoCode, setPromoCode] = useState('')
  const [termsAccepted, setTermsAccepted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted) {
    return (
      <div className="card p-8 animate-pulse space-y-4">
        <div className="h-24 bg-muted rounded-lg" />
        <div className="h-12 bg-muted rounded-lg" />
        <div className="h-12 bg-muted rounded-lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Ride Summary Card */}
      <div className="card p-6 bg-gradient-to-br from-secondary/5 to-accent/5">
        <h3 className="text-lg font-display font-bold text-primary mb-4">
          Ride Summary
        </h3>
        <div className="space-y-3">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-2">
              <Icon name="MapPinIcon" size={16} className="text-secondary mt-1" />
              <div>
                <p className="text-sm font-body font-semibold text-foreground">
                  Starting Point
                </p>
                <p className="text-xs text-muted-foreground font-body">
                  123 Main Street, Downtown
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-2">
              <Icon name="FlagIcon" size={16} className="text-accent mt-1" />
              <div>
                <p className="text-sm font-body font-semibold text-foreground">
                  Destination
                </p>
                <p className="text-xs text-muted-foreground font-body">
                  456 Oak Avenue, Uptown
                </p>
              </div>
            </div>
          </div>
          <div className="pt-3 border-t border-border flex items-center justify-between">
            <span className="text-sm font-body text-foreground/70">
              Premium Scooter
            </span>
            <span className="text-xl font-display font-bold text-primary">
              $4.00
            </span>
          </div>
        </div>
      </div>

      {/* Payment Method Selection */}
      <div className="card p-6">
        <h3 className="text-lg font-display font-bold text-primary mb-4">
          Payment Method
        </h3>
        <div className="space-y-3">
          {paymentMethods.map((method) => (
            <div
              key={method.id}
              onClick={() => setSelectedPayment(method.id)}
              className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                selectedPayment === method.id
                  ? 'border-accent bg-accent/5' :'border-border hover:border-accent/50'
              }`}
            >
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  selectedPayment === method.id
                    ? 'border-accent' :'border-muted-foreground'
                }`}
              >
                {selectedPayment === method.id && (
                  <div className="w-3 h-3 rounded-full bg-accent" />
                )}
              </div>
              <Icon
                name={method.icon}
                size={24}
                className={
                  selectedPayment === method.id
                    ? 'text-accent' :'text-muted-foreground'
                }
              />
              <span className="font-body font-medium text-foreground">
                {method.name}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Promo Code */}
      <div className="card p-6">
        <h3 className="text-lg font-display font-bold text-primary mb-4">
          Promo Code
        </h3>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Enter promo code"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value)}
            className="flex-1 px-4 py-3 rounded-lg border border-border bg-input focus:ring-2 focus:ring-secondary focus:border-transparent outline-none transition-all font-body"
          />
          <button className="px-6 py-3 rounded-lg bg-muted hover:bg-accent hover:text-accent-foreground transition-all font-body font-semibold">
            Apply
          </button>
        </div>
      </div>

      {/* Terms & Conditions */}
      <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50">
        <input
          type="checkbox"
          id="terms"
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.target.checked)}
          className="mt-1 w-5 h-5 rounded border-border text-accent focus:ring-accent"
        />
        <label htmlFor="terms" className="text-sm font-body text-foreground/70 cursor-pointer">
          I agree to the{' '}
          <a href="/terms" className="text-secondary hover:underline">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="/privacy" className="text-secondary hover:underline">
            Privacy Policy
          </a>
        </label>
      </div>

      {/* Confirm Button */}
      <button
        disabled={!termsAccepted}
        className="w-full btn-primary text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Icon name="CheckCircleIcon" size={24} className="inline mr-2" />
        Confirm & Unlock Scooter
      </button>

      {/* Security Note */}
      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground font-body">
        <Icon name="LockClosedIcon" size={14} />
        <span>Your payment information is secure and encrypted</span>
      </div>
    </div>
  )
}